import { MockHttpError, UnmatchedMockRouteError, routeMockRequest } from "./router";

const API_PREFIX = "/api/v1";
const MOCK_DELAY_MS = 45;
const opaqueKeys = new Set(["schema", "schema_json", "ui_schema", "ui_schema_json", "extra_data"]);

function snakeKey(key: string) {
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function snakeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snakeValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => {
    const snake = snakeKey(key);
    return [snake, opaqueKeys.has(snake) ? item : snakeValue(item)];
  }));
}

async function requestBody(options: RequestInit) {
  if (options.body instanceof FormData) return options.body;
  if (typeof options.body !== "string" || options.body === "") return undefined;
  return JSON.parse(options.body) as unknown;
}

function jsonResponse(body: unknown, status = 200, headers?: HeadersInit) {
  return new Response(JSON.stringify({ data: snakeValue(body) }), {
    status,
    headers: { "Content-Type": "application/json; charset=utf-8", ...headers },
  });
}

export async function mockFetch(input: RequestInfo | URL, options: RequestInit = {}) {
  await new Promise((resolve) => window.setTimeout(resolve, MOCK_DELAY_MS));
  const url = new URL(typeof input === "string" ? input : input instanceof URL ? input.href : input.url, window.location.origin);
  const path = url.pathname.startsWith(API_PREFIX) ? url.pathname.slice(API_PREFIX.length) || "/" : url.pathname;
  const method = (options.method || (input instanceof Request ? input.method : "GET")).toUpperCase();
  try {
    const result = routeMockRequest({ method, path, query: url.searchParams, body: await requestBody(options) });
    if (result.status === 204) return new Response(null, { status: 204, headers: result.headers });
    return jsonResponse(result.body, result.status, result.headers);
  } catch (error) {
    if (error instanceof UnmatchedMockRouteError) throw error;
    if (error instanceof MockHttpError) {
      return new Response(JSON.stringify({ error: { code: error.code, message: error.message } }), {
        status: error.status,
        headers: { "Content-Type": "application/json; charset=utf-8" },
      });
    }
    throw error;
  }
}
